import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as PR from "../prime-modules/index";
import { useDispatch, useSelector } from "react-redux";
import { responsiveOptions } from "../constants";
import { getCatalogsList, getCountriesList } from "../services/api";
import { countryActions } from "../store/country";
import { cartFilterActions } from "../store/cart";
import {
  geteSIMsDescription,
  renderDurationIfPlural,
  formatCurrency,
  renderSpeed,
  setDataAmount,
  setNumberPrecision,
} from "../utils/reuse";
import { track } from "@plausible-analytics/tracker";
import MakePayment from "./payment/MakePayment";
import { BundleListLoading } from "./skeletonLoading/BundleListLoading";
import { esimIccidActions } from "../store/esim";
import allCountries from "../assets/data/countries.json";
import { regionalGlobalCountries } from "../assets/data/regionalGlobalCountries";
import { comingsoon } from "../assets/images";
import { renderRegionalCountryImage } from "../assets/images/eSIMCrewCountryImages/eSIMCrewImages";
import { flag } from "country-emoji";
import RoamingCountries from "../shared/components/RoamingCountries";
import { useToast } from "../context/ToastContext";
import { Link, useLocation, useNavigate } from "react-router-dom";

const BundlesList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const getCartData = useSelector((state) => state.cart.catalogCart);
  const getCountry = useSelector((state) => state.country.country);
  const getIPCountry = useSelector((state) => state.country.IPCountry);
  const userData = useSelector(state => state.auth.sessionData);
  
  const affiliateTrackingData = useSelector(
    (state) => state.affiliateTracking.affiliateTrackingInfo
  );
  const headers = useMemo(() => {
    return { afid: affiliateTrackingData };
  }, [affiliateTrackingData]);
  const { pathname } =  useLocation();
  const { showToast, labels } = useToast();
  const [loading, setLoading] = useState(false);
  const [country, setCountry] = useState("");
  const [countryData, setCountryData] = useState("");
  const [countries, setCountries] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [catalogData, setCatalogData] = useState({});
  const [bundleInfoVisible, setBundleInfoVisible] = useState(false);
  const [displayNetworks, setDisplayNetworks] = useState(false);
  const [doNotSell, setDoNotSell] = useState(false)

  const [chackoutFormState, setCheckoutFormState] = useState(false);

  // Refs to prevent duplicate API calls
  const isCallingCatalogsRef = useRef(false);
  const lastCatalogRequestRef = useRef(null);

  const getCatalogs = useCallback(() => {
    // Only proceed if country is set and not already calling
    if (!country || country === "" || isCallingCatalogsRef.current) {
      return;
    }

    const catalogCountry = encodeURIComponent(country);
    const queryParameters = `countries=${catalogCountry}&direction=asc&orderBy=price`;

    // Check if this is the same request as the last one
    if (lastCatalogRequestRef.current === queryParameters) {
      return;
    }

    // Mark that we're making a call and store the request params
    isCallingCatalogsRef.current = true;
    lastCatalogRequestRef.current = queryParameters;

    const getCatalogList = (response) => {
      if (response.result === "SUCCESS") {
        const catalogList =
          response.data && response.data.bundles ? response.data.bundles : [];
        setDoNotSell(response?.data?.doNotSell ?? false)
        if (catalogList.length > 0) {
          let sortedCatalogs = catalogList.sort((a, b) => a.price - b.price);
            const limitedDataPlans = sortedCatalogs
              ?.filter((d) => d?.dataAmount !== -1)
              ?.sort((a, b) => a?.dataAmount - b?.dataAmount);
            const unlimitedDataPlans = sortedCatalogs
              ?.filter((d) => d?.dataAmount === -1)
              ?.sort((a, b) => a?.duration - b?.duration);
              sortedCatalogs = [...limitedDataPlans, ...unlimitedDataPlans];
            setCatalogs(sortedCatalogs);
          } else {
            showToast('info', 'ESMBND-02') // home.esim.plansError
          }
        } else {
          const error = response.error;
          if(error.errorMsg !== "GNRMSG-03"){
            showToast(error.severity, error.errorMsg)
          }
        }
        setLoading(false);
        isCallingCatalogsRef.current = false;
    };

    setLoading(true);
    setCatalogs([]);
    getCatalogsList(queryParameters, headers, dispatch, getCatalogList);
  }, [country, dispatch, headers, showToast]);

  const getNetworks = (countries) => {
    let networks = [];
    if (countries && countries.length > 0) {
      const networksList = countries[0]["networks"];
      if (networksList && networksList.length > 0) {
        networks = networksList;
      }
    }
    return networks;
  };

  const setNetworks = (countries) => {
    const networks = getNetworks(countries);

    return (
      networks &&
      (networks.length === 0 ? (
        <span>N/A</span>
      ) : (
        <span className="networkData">
          {networks.map((n, index) => (
            <span key={index} className="catalogNetworks">
              {n.name} {n.speeds && "(" + renderSpeed(n.speeds) + ")"}
            </span>
          ))}
        </span>
      ))
    );
  };

  const clearStoreContent = useCallback(() => {
    dispatch(cartFilterActions.deleteCatalogCart());
    dispatch(cartFilterActions.setCatalogCartAmount(0));
  }, [dispatch]);

  const trackCountry = useCallback((countryName) => {
    track("country", { props: { country: countryName } });
  }, []);

  const getCountryData = useCallback(() => {
    if (countryData === "") {
      setCountryData(null);
      const getResponse = (response) => {
        if (response.result === "SUCCESS") {
          const responseCountry = response.data ? response.data : [];
             const addGeogTypeToAllCountries = allCountries.map((c) => {
              c.geog_type = "Local";
              return c;
            });
           const allCountriesList = [...responseCountry, ...addGeogTypeToAllCountries, ...regionalGlobalCountries]
            const uniqueCountriesList = allCountriesList.reduce(
              (acc, current) => {
                const x = acc.find((item) => item.iso === current.iso);
                if (!x) {
                  return acc.concat([current]);
                } else {
                  return acc;
                }
              },
              [],
            );
            const countriesData = [...uniqueCountriesList].sort((a,b) => a.name.localeCompare(b.name))
          if (countriesData.length > 0) {
            const countriesList = countriesData.map((c) => ({
              name: c.name.split("(")[0].trim(),
              region: c.region,
              iso: c.iso,
            }));
         
            setCountries(countriesList);

            const findCountry = countriesList.find(
              (c) => c.iso === response.ipcountry
            );
            let defaultCountry =
              findCountry && findCountry.iso
                ? findCountry.iso
                : countriesList[0]["iso"];
            if (getIPCountry !== response.ipcountry) {
              clearStoreContent();
              dispatch(countryActions.setIpCountry(response.ipcountry));
            } else if (getCountry && getCountry.iso) {
              defaultCountry = countriesList.find(
                (c) => c.iso === getCountry.iso
              )
                ? getCountry.iso
                : countriesList[0]["iso"];
            }
            setCountry(defaultCountry);
            const getFilteredCountry = countriesList.find(
              (val) => val.iso === defaultCountry
            );
            trackCountry(getFilteredCountry.name);
            dispatch(countryActions.setCountry(getFilteredCountry));

            setCountryData(defaultCountry);
          } else {
            showToast("info", "ESMBND-03") // countriesError
          }
        } else {
          const error = response.error;
          showToast(error.severity, error.errorMsg);
        }
      };
      const url = "countries";
      getCountriesList(url, headers, dispatch, getResponse);
    }
  }, [clearStoreContent, countryData, dispatch, getCountry, getIPCountry, headers, showToast, trackCountry]);

  useEffect(() => {
    getCountryData();
  }, [getCountryData]);

  useEffect(() => {
    getCatalogs();
  }, [getCatalogs]);

  const catalogPurchase = (catalogData) => {
    const catalogName = catalogData.name;
    const catalogPrice = setNumberPrecision(catalogData.price);

    const getCartObj = { ...getCartData };
    let setCartData = {};
    if (getCartObj !== catalogName) {
      setCartData = {
        name: catalogName,
        description: catalogData.description,
        quantity: 1,
        price: +catalogPrice,
        speed: catalogData.speed,
        duration: catalogData.duration,
        dataAmount: catalogData.dataAmount,
        dataAmountForDisplay: catalogData.dataAmountForDisplay,
        priceCurrency: catalogData?.priceCurrency
      };
      dispatch(cartFilterActions.setCatalogCart(setCartData));
      dispatch(cartFilterActions.setCatalogCartAmount(+catalogPrice));
    }

    const catalogCountry = catalogData.countries[0]["country"]["iso"];
    const getFilteredCountry = countries.find((x) => x.iso === catalogCountry);
    dispatch(countryActions.setCountry(getFilteredCountry));
    dispatch(esimIccidActions.deleteEsim());
    setCountry(catalogCountry);
    navigate(pathname, {replace: true, state: {}})

    if (window?.webviewData?.jwt) {
      // This is for esim  mini app
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          bundleData: setCartData,
          sessionId: userData?.sessionId,
        })
      );
    } else {
    setCheckoutFormState(true);
    }
  };

  const displayCatalogDetails = (catalogData) => {
    setCatalogData(catalogData);
    setBundleInfoVisible(true);
  };

  const productTemplate = (rowData) => {
    const countryImageIso = rowData.countries.map(img => img.country.iso);
    const description = geteSIMsDescription(rowData);
    rowData.description = description;
    return (
      <div className="border-1 surface-border plan-box">
        <div className="flex align-items-center gap-3 mb-4">
          {renderRegionalCountryImage(getCountry?.iso)
          ? <img src={renderRegionalCountryImage(getCountry.iso)} alt={getCountry.iso} className="country-image"/>
          : countryImageIso.map((iso, index) => (
            <div key={index} className="country-image">
              <div className="flag">{flag(iso)}</div>
            </div>
          ))}
          <h3>{description}{rowData?.fup && <FupInfo fup={rowData.fup} />} </h3>
        </div>
        <div className="flex align-items-center justify-content-between mb-5">
          <h2>{formatCurrency(rowData?.priceCurrency, rowData.price)}</h2>
          <i
            className="pi pi-eye"
            title={labels.LBL0016}
            onClick={() => {
              displayCatalogDetails(rowData);
            }}
          ></i>
        </div>
        {rowData?.dataAmountDisplay && (
          <p className="pi pi-check">
            <span className="font-semibold">
              {setDataAmount(rowData, labels)}
            </span>
          </p>
        )}
        {/* {rowData.speed && (
          <p className="pi pi-check">
            <span>{renderSpeed(rowData.speed)}</span>
          </p>
        )} */}
        <p className="pi pi-check">
          <span>{labels.LBL0017} - {renderDurationIfPlural(rowData.duration, labels)} </span>
        </p>
        <p className="pi pi-check">
          <span>{labels.LBL0019}  {formatCurrency(rowData?.priceCurrency, rowData.price)}</span>
        </p>
        <p className="info-text">{labels.LBL0020}</p>
        <div className="flex align-items-center gap-3 mt-4">
          <PR.Button
            label={labels.LBL0021}
            disabled={doNotSell}
            className="goto-plans-button p-button-rounded"
            onClick={() => catalogPurchase(rowData)}
          />
          <Link className="view-link" to={"/bundle-details/"+ rowData?.name} >{labels?.LBL0111}</Link>
        </div>
      </div>
    );
  };

  const onCountryChange = (e) => {
    if (e.value) {
      setCountry(e.value);
    }
  };

  const onCountryHide = (e) => {
    if (getCountry.iso !== country) {
      clearStoreContent();
      const getFilteredCountry = countries.find((x) => x.iso === country);
      if (getFilteredCountry) {
        dispatch(countryActions.setCountry(getFilteredCountry));
        trackCountry(getFilteredCountry.name);
      }
    }
  };

  const onHide = () => {
    setBundleInfoVisible(false);
    setCatalogData({});
  };

  const dropdownCountryTemplate = (option, props) => {
    if (option) {
      return (
        <div className="flex align-items-center">
          {
            renderRegionalCountryImage(option.iso)
              ? <div className="country-image">
                <img alt={option.iso} src={renderRegionalCountryImage(option.iso)} className="flag-img" />
              </div>
              : <div className="country-image">
                <div className="flag">
                  {
                    flag(option.iso)
                  }
                </div>
              </div>
          }

          <div>{option.name}</div>
        </div>
      );
    }

    return <span>{props.placeholder}</span>;
};

  return (
    <>
      <div className="country-dropdown">
        <span className="label-span">{labels.LBL0022}</span>
        <PR.Dropdown
          filter
          value={country}
          options={countries}
          onChange={(e) => onCountryChange(e)}
          onHide={(e) => onCountryHide(e)}
          resetFilterOnHide
          optionLabel="name"
          optionValue="iso"
          placeholder={labels.LBL0023}
          valueTemplate={dropdownCountryTemplate}
          itemTemplate={dropdownCountryTemplate}
        />
      </div>
      { doNotSell && <p className='text-center text-xs err-msg  mt-10'><sup className='err-msg'>*</sup> {labels?.LBL0171}</p>}
      <PR.Dialog
        header={labels.LBL0016}
        className="bundle-dialog"
        visible={bundleInfoVisible}
        blockScroll={true}
        draggable={false}
        breakpoints={{ "960px": "85vw" }}
        style={{ width: "38vw" }}
        onHide={onHide}
      >
        <div className="data-item">
          <div className="data-item-header">
            <div className="grid grid-nogutter mt-2 align-items-center">
              <div className="col-12 md:col-9 lg:col-9">
                <h3>
                  <span className="vertical-align-super">
                    {catalogData.description}
                  </span>
                </h3>
              </div>
              <div className="col-12 md:col-3 lg:col-3 text-right">
                {catalogData.price && (
                  <h3 className="color-h3">{formatCurrency(catalogData?.priceCurrency, catalogData.price)}</h3>
                )}
              </div>
            </div>
          </div>
          <div className="data-item-content">
            <table className="w-12">
              <tbody>
                <tr>
                  <td>{labels.LBL0024}</td>
                  <td className="text-right">
                    {setDataAmount(catalogData, labels)} {catalogData?.fup && <FupInfo fup={catalogData.fup} />}
                  </td>
                </tr>
                <tr>
                  <td>{labels.LBL0025}</td>
                  <td className="text-right">
                    {renderDurationIfPlural(catalogData.duration, labels)}
                  </td>
                </tr>
                {catalogData.speed && (
                  <tr>
                    <td>{labels.LBL0026}</td>
                    <td className="text-right">
                      {renderSpeed(catalogData.speed)}
                    </td>
                  </tr>
                )}
                <tr>
                  <td>{labels.LBL0027}</td>
                  <td className="text-right">
                    {catalogData.countries && catalogData?.roamingEnabled?.length === 0
                      ? catalogData?.countries[0]?.country?.name.replace(" (Special Administrative Region of China)", "")
                      : <RoamingCountries roaming={catalogData.roamingEnabled} />}
                  </td>
                </tr>
                <tr
                  className={
                    getNetworks(catalogData.countries).length > 0
                      ? "network-tr"
                      : ""
                  }
                >
                  <td>{labels.LBL0028}</td>
                  {catalogData.geog_type === "Global" ||
                  catalogData.geog_type === "Regional" ? (
                    <td>
                      <span className="roamingData">
                        {labels.LBL0029} (
                        {catalogData.roamingEnabled &&
                          catalogData.roamingEnabled.length}{" "}
                        {labels.LBL0030})
                        <i
                          className="roaming-tooltip pi pi-info-circle info-btn"
                          title={labels.LBL0170}
                          onClick={() => setDisplayNetworks(true)}
                        ></i>
                        <PR.Dialog
                          header={labels.LBL0031}
                          className="roamingColumnsSection bundle-dialog"
                          visible={displayNetworks}
                          blockScroll={true}
                          draggable={false}
                          onHide={() => setDisplayNetworks(false)}
                          style={{ width: "60vw" }}
                          resizable={false}
                        >
                          <div>
                            {catalogData.roamingEnabled &&
                              catalogData.roamingEnabled.map(
                                (column, columnIndex) => (
                                  <div key={columnIndex}>
                                    <p>
                                      <strong>
                                        {column.country && column.country.name
                                        ? column.country.name.replace(" (Special Administrative Region of China)", "")
                                        : ""}
                                      </strong>
                                    </p>
                                    <ul key={columnIndex}>
                                      {column.networks &&
                                        column.networks.map(
                                          (item, itemIndex) => (
                                            <li
                                              key={itemIndex}
                                              className="mb-2"
                                            >
                                              {item.name} (
                                              {item.speeds &&
                                                item.speeds.join(",")}
                                              )
                                            </li>
                                          )
                                        )}
                                    </ul>
                                  </div>
                                )
                              )}
                          </div>
                        </PR.Dialog>
                      </span>
                    </td>
                  ) : (
                    <td>{setNetworks(catalogData.countries)}</td>
                  )}
                </tr>
                <tr className="last-row">
                  <td className="text-left">
                    <PR.Button
                      label={labels.LBL0032}
                      disabled={doNotSell}
                      className="buynow-btn"
                      onClick={() => {
                        catalogPurchase(catalogData);
                      }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </PR.Dialog>
      <PR.Dialog
        visible={chackoutFormState}
        className="forms-dialog payment-dialog"
        blockScroll={true}
        draggable={false}
        style={{ width: "75%" }}
        onHide={() => setCheckoutFormState(false)}
      >
        <MakePayment setCheckoutFormState={setCheckoutFormState} />
      </PR.Dialog>
      {loading ? (
        BundleListLoading()
      ) : catalogs.length > 0 ? (
        <PR.Carousel
          value={catalogs}
          numVisible={4}
          numScroll={1}
          responsiveOptions={responsiveOptions}
          itemTemplate={productTemplate}
        />
      ) : (
        !loading && 
        <PR.Image src={comingsoon} alt="coming soon" width="100%"/>
      )}
    </>
  );
};

export default BundlesList;

export const FupInfo = ({fup}) => <i className="ml-2 pi pi-info-circle fup-circle" title={fup} />;